import React from "react";
import PropTypes from "prop-types";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import { dayjs } from "utils/time.jsx";

function PlanDetailsCard({ recommendationData, planDetails }) {
  if (!recommendationData) {
    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <div>
            <p>Please select a plan to view details.</p>
          </div>
        </GridItem>
      </GridContainer>
    );
  }

  const endDate = new Date(recommendationData.end_time);
  const now = new Date();
  const isPast = endDate < now;
  const planStatus = isPast
    ? "PAST"
    : recommendationData.active
      ? "ACTIVE"
      : "INACTIVE";
  const meta =
    (planDetails && planDetails.meta) ||
    (recommendationData && recommendationData.meta) ||
    null;

  const getTrainingColor = trainingType => {
    const colorMap = {
      Rest: "#999999",
      L: "#9c27b0",
      E: "#4caf50",
      T: "#ff9800",
      R: "#f44336",
      I: "#e91e63",
      M: "#2196f3"
    };
    return colorMap[trainingType] || "#00acc1";
  };

  const getWeekdayNameAndDate = (planStartDateValue, weekNumber, dayIndex) => {
    if (!planStartDateValue) {
      return null;
    }
    const trainingDay = dayjs(planStartDateValue)
      .add((weekNumber - 1) * 7 + dayIndex, "day")
      .toDate();
    return trainingDay.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
  };

  const isItToday = (planStartDateValue, weekNumber, dayIndex) => {
    if (!planStartDateValue) {
      return false;
    }
    const trainingDay = dayjs(planStartDateValue).add(
      (weekNumber - 1) * 7 + dayIndex,
      "day"
    );
    return trainingDay.isSame(dayjs(), "day");
  };

  const planStartDate =
    (planDetails && planDetails.duration && planDetails.duration.start) ||
    recommendationData.start_time;

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <div
          style={{
            backgroundColor: "#fff",
            padding: "16px",
            borderRadius: "8px"
          }}
        >
          <div>
            <h4 style={{ margin: 0, color: "#333" }}>
              {`${(meta && meta.training_name) ||
                recommendationData.recommendation_type ||
                "Health Recommendation"} Plan - ${planStatus}`}
            </h4>
          </div>
          <div>
            {/* Plan Overview */}
            <GridContainer>
              <GridItem xs={12} sm={6} md={4}>
                <h6 style={{ color: "#333" }}>Trainee & Advisor</h6>
                <p style={{ color: "#333" }}>
                  <strong>Trainee:</strong>{" "}
                  {(planDetails && planDetails.user) || "N/A"}
                </p>
                <p style={{ color: "#333" }}>
                  <strong>Advisor:</strong>{" "}
                  {(planDetails && planDetails.advisor) ||
                    recommendationData.advisor ||
                    "N/A"}
                </p>
                {planDetails &&
                  planDetails.total_weeks && (
                    <p style={{ color: "#333" }}>
                      <strong>Duration:</strong> {planDetails.total_weeks} weeks
                    </p>
                  )}
                {recommendationData.created_at && (
                  <p style={{ color: "#333" }}>
                    <strong>Created:</strong>{" "}
                    {new Date(
                      recommendationData.created_at
                    ).toLocaleDateString()}
                  </p>
                )}
              </GridItem>
              <GridItem xs={12} sm={6} md={4}>
                <h6 style={{ color: "#333" }}>Plan Duration</h6>
                <p style={{ color: "#333" }}>
                  <strong>Start Date:</strong>{" "}
                  {(planDetails &&
                    planDetails.duration &&
                    planDetails.duration.start) ||
                    recommendationData.start_time}
                </p>
                <p style={{ color: "#333" }}>
                  <strong>End Date:</strong>{" "}
                  {(planDetails &&
                    planDetails.duration &&
                    planDetails.duration.end) ||
                    recommendationData.end_time}
                </p>
                <p style={{ color: "#333" }}>
                  <strong>Status:</strong> {planStatus}
                </p>
              </GridItem>
              <GridItem xs={12} sm={6} md={4}>
                <h6 style={{ color: "#333" }}>Training Goals</h6>
                {meta ? (
                  <React.Fragment>
                    {meta.target_distance && (
                      <p style={{ color: "#333" }}>
                        <strong>Target Distance:</strong> {meta.target_distance}{" "}
                        km
                      </p>
                    )}
                    {meta.target_date && (
                      <p style={{ color: "#333" }}>
                        <strong>Target Date:</strong>{" "}
                        {new Date(meta.target_date).toLocaleDateString()}
                      </p>
                    )}
                    {meta.current_vdot && (
                      <p style={{ color: "#333" }}>
                        <strong>Current VDOT:</strong> {meta.current_vdot}
                      </p>
                    )}
                    {meta.expected_race_vdot && (
                      <p style={{ color: "#333" }}>
                        <strong>Expected Race VDOT:</strong>{" "}
                        {meta.expected_race_vdot}
                      </p>
                    )}
                    {meta.vdot_paces_for_1k && (
                      <div style={{ color: "#333", marginTop: "8px" }}>
                        <strong>Training Paces (1K):</strong>
                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "13px",
                            lineHeight: "1.6",
                            paddingLeft: "30px"
                          }}
                        >
                          {Array.isArray(meta.vdot_paces_for_1k) ? (
                            <React.Fragment>
                              {meta.vdot_paces_for_1k[0] && (
                                <div style={{ color: getTrainingColor("E") }}>
                                  <strong>Easy:</strong>{" "}
                                  {meta.vdot_paces_for_1k[0]}
                                </div>
                              )}
                              {meta.vdot_paces_for_1k[1] && (
                                <div style={{ color: getTrainingColor("M") }}>
                                  <strong>Marathon:</strong>{" "}
                                  {meta.vdot_paces_for_1k[1]}
                                </div>
                              )}
                              {meta.vdot_paces_for_1k[2] && (
                                <div style={{ color: getTrainingColor("T") }}>
                                  <strong>Threshold:</strong>{" "}
                                  {meta.vdot_paces_for_1k[2]}
                                </div>
                              )}
                              {meta.vdot_paces_for_1k[3] && (
                                <div style={{ color: getTrainingColor("I") }}>
                                  <strong>Interval:</strong>{" "}
                                  {meta.vdot_paces_for_1k[3]}
                                </div>
                              )}
                              {meta.vdot_paces_for_1k[4] && (
                                <div style={{ color: getTrainingColor("R") }}>
                                  <strong>Repetitions:</strong>{" "}
                                  {meta.vdot_paces_for_1k[4]}
                                </div>
                              )}
                            </React.Fragment>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <p style={{ color: "#333" }}>
                      <strong>Type:</strong>{" "}
                      {(planDetails && planDetails.type) ||
                        recommendationData.recommendation_type ||
                        "N/A"}
                    </p>
                    <p style={{ color: "#333" }}>
                      <strong>Total Weeks:</strong>{" "}
                      {(planDetails && planDetails.total_weeks) || "N/A"}
                    </p>
                    <p style={{ color: "#333" }}>
                      <strong>Active:</strong>{" "}
                      {recommendationData.active ? "Yes" : "No"}
                    </p>
                  </React.Fragment>
                )}
              </GridItem>
            </GridContainer>

            {/* Weekly Breakdown */}
            {planDetails &&
              planDetails.weekly_breakdown &&
              planDetails.weekly_breakdown.length > 0 && (
                <div style={{ marginTop: "30px" }}>
                  <h5
                    style={{
                      marginBottom: "20px",
                      borderBottom: "2px solid #ddd",
                      paddingBottom: "10px"
                    }}
                  >
                    Weekly Training Breakdown
                  </h5>
                  {planDetails.weekly_breakdown.map((week, idx) => {
                    const weekCapForDays = week.training_days
                      .filter(
                        day =>
                          typeof day.max_capacity === "number" &&
                          day.max_capacity > 0
                      )
                      .map(day => day.max_capacity);
                    const minCap = weekCapForDays.length
                      ? Math.min.apply(null, weekCapForDays)
                      : 0;
                    const maxCap = weekCapForDays.length
                      ? Math.max.apply(null, weekCapForDays)
                      : 0;
                    const weeklyMileage = `${minCap.toFixed(
                      0
                    )} - ${maxCap.toFixed(0)}`;

                    return (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          marginBottom: "20px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          overflow: "hidden",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                        }}
                      >
                        {/* First Column - Week Summary */}
                        <div
                          style={{
                            flex: "0 0 180px",
                            padding: "16px",
                            backgroundColor: "rgba(0, 172, 193, 0.08)",
                            borderRight: "2px solid rgba(0, 172, 193, 0.25)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px"
                          }}
                        >
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#00acc1"
                            }}
                          >
                            Week {week.week}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#555",
                              marginTop: "8px"
                            }}
                          >
                            <strong>Focus:</strong>
                            <div style={{ fontSize: "13px", color: "#666" }}>
                              {week.focus}
                            </div>
                          </div>
                          <div style={{ fontSize: "14px", color: "#555" }}>
                            <strong>Intensity:</strong>
                            <div style={{ fontSize: "13px", color: "#666" }}>
                              {week.intensity}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#555",
                              marginTop: "8px",
                              paddingTop: "8px",
                              borderTop: "1px solid rgba(0,0,0,0.1)"
                            }}
                          >
                            <strong>Weekly Mileage:</strong>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                color: "#00acc1"
                              }}
                            >
                              {weeklyMileage} km
                            </div>
                          </div>
                        </div>

                        {/* Training Days Columns */}
                        <div
                          style={{
                            flex: "1",
                            display: "flex",
                            flexDirection: "column"
                          }}
                        >
                          {/* Day Headers */}
                          <div
                            style={{
                              display: "flex",
                              borderBottom: "1px solid #e0e0e0",
                              backgroundColor: "#f9f9f9"
                            }}
                          >
                            {week.training_days.map((day, dayIdx) => (
                              <div
                                key={dayIdx}
                                style={{
                                  flex: "1",
                                  padding: "8px 4px",
                                  textAlign: "center",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  color: "#666",
                                  borderRight:
                                    dayIdx < 6 ? "1px solid #e0e0e0" : "none"
                                }}
                              >
                                {getWeekdayNameAndDate(
                                  planStartDate,
                                  week.week,
                                  dayIdx
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Training Descriptions */}
                          <div style={{ display: "flex", flex: "1" }}>
                            {week.training_days.map((day, dayIdx) => {
                              const isToday = isItToday(
                                planStartDate,
                                week.week,
                                dayIdx
                              );

                              const planDetails = day.plan
                                ? day.plan.replace(/^"+|"+$/g, "").trim()
                                : null;

                              return (
                                <div
                                  key={dayIdx}
                                  style={{
                                    flex: "1",
                                    padding: "12px 8px",
                                    borderRight:
                                      dayIdx < 6 ? "1px solid #e0e0e0" : "none",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    backgroundColor:
                                      day.training_type === "Rest"
                                        ? "#fafafa"
                                        : "white",
                                    borderWidth: isToday ? "1px" : "0",
                                    borderStyle: isToday ? "solid" : "none",
                                    borderColor: isToday ? "#00acc1" : "none",
                                    boxShadow: isToday
                                      ? "0 0 8px rgba(0, 172, 193, 0.6)"
                                      : "none"
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                      color: getTrainingColor(
                                        day.training_type
                                      ),
                                      marginBottom: "6px",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: `1px solid ${getTrainingColor(
                                        day.training_type
                                      )}`,
                                      backgroundColor: `${getTrainingColor(
                                        day.training_type
                                      )}15`
                                    }}
                                  >
                                    {day.training_type_name}
                                  </div>
                                  {planDetails && (
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#666",
                                        marginTop: "8px",
                                        lineHeight: "1.4"
                                      }}
                                    >
                                      {planDetails
                                        .split(";;;")
                                        .map(
                                          (part, pIdx) =>
                                            part.trim() ? (
                                              <div key={pIdx}>
                                                {part.trim()}
                                              </div>
                                            ) : null
                                        )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            {/* Legacy Health Advices (if no weekly breakdown) */}
            {(!planDetails ||
              !planDetails.weekly_breakdown ||
              planDetails.weekly_breakdown.length === 0) &&
              recommendationData.health_advices &&
              recommendationData.health_advices.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <h6 style={{ color: "#333" }}>Health Advices</h6>
                  {recommendationData.health_advices.map((advice, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: "15px",
                        padding: "10px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "5px"
                      }}
                    >
                      <h6 style={{ marginBottom: "10px", color: "#333" }}>
                        {advice.name}
                      </h6>
                      {advice.details &&
                        advice.details.map((detail, dIdx) => (
                          <div
                            key={dIdx}
                            style={{
                              marginLeft: "20px",
                              marginBottom: "8px"
                            }}
                          >
                            <p style={{ color: "#333" }}>
                              <strong>{detail.name}:</strong>{" "}
                              {detail.description}
                            </p>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </GridItem>
    </GridContainer>
  );
}

PlanDetailsCard.propTypes = {
  recommendationData: PropTypes.shape({
    end_time: PropTypes.string.isRequired,
    active: PropTypes.bool,
    recommendation_type: PropTypes.string,
    advisor: PropTypes.string,
    created_at: PropTypes.string,
    start_time: PropTypes.string,
    health_advices: PropTypes.array
  }),
  planDetails: PropTypes.shape({
    type: PropTypes.string,
    user: PropTypes.string,
    advisor: PropTypes.string,
    total_weeks: PropTypes.number,
    duration: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string
    }),
    weekly_breakdown: PropTypes.array
  })
};

export default PlanDetailsCard;
